import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import type { CreateGroupMappingBody, GroupMappingsResponse } from "@shared/types/groupAccess";
import type { OverrideRole } from "@shared/permissions";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/lib/auth/AuthContext";
import { instanceHeaders } from "@/hooks/use-access";
import {
  clearStepUpAttempt,
  isMfaRequired,
  isStepUpRequired,
  noteStepUpAttempt,
  providerDidNotAskMessage,
  recentStepUpAttempt,
  stepUpRefusedMessage,
} from "@/lib/auth/mfaStepUp";

/**
 * Access that follows the customer's Entra groups (group access, 2026-09-28): the tenant's
 * directory groups, its "Require a directory group" switch, and the per-person override.
 *
 * Every write here needs a recent sign-in (FR-14). A 401 `REAUTH_REQUIRED` holds the write, opens
 * the re-authentication dialog, and replays the SAME write once identity is confirmed, as the
 * destination save does (`use-binding.ts`). When the provider has to leave the page to confirm,
 * the held write is not carried across the redirect: these are one-click changes, and the admin
 * makes them again on return.
 */

export function groupMappingsPath(tenantId: string): string {
  return `/api/v2/tenants/${tenantId}/group-mappings`;
}

/** Whether this deployment has group access on (`GET /me` `features.groupAccess`). */
export function useGroupAccessEnabled(): boolean {
  const { me } = useTenantInstance();
  return me?.features?.groupAccess === true;
}

export function useGroupMappings(tenantId: string | null, enabled: boolean) {
  return useQuery<GroupMappingsResponse>({
    queryKey: [tenantId ? groupMappingsPath(tenantId) : "group-mappings:none"],
    queryFn: ({ signal }) =>
      apiRequest<GroupMappingsResponse>(groupMappingsPath(tenantId!), { signal }),
    enabled: enabled && tenantId !== null,
  });
}

/** One write held across the re-authentication dialog. */
export interface ReauthHold {
  isOpen: boolean;
  isRetrying: boolean;
  error: string | null;
  confirm: () => void;
  cancel: () => void;
  /** Admin MFA: which refusal is held, for the dialog's copy. */
  code: "REAUTH_REQUIRED" | "MFA_REQUIRED";
  /** The provider already came back without a second factor: no second redirect. */
  blocked: boolean;
  /** "Do not ask again on this browser" for the dialog; 0 days hides it. */
  remember: { days: number; account: string | null };
}

/**
 * Runs a write; on `REAUTH_REQUIRED` or `MFA_REQUIRED` (admin MFA) keeps it and opens the dialog,
 * and `confirm` replays it once identity is confirmed. Any other failure is thrown to the caller.
 * An `MFA_REQUIRED` right after this account was sent for the second step (the provider asked for
 * none) opens the dialog blocked, with the reason, and never redirects again.
 *
 * `run` resolves to null when the write was held (nothing was written yet): the caller must not
 * report success then. `onReplayed` runs once the held write lands after the re-authentication,
 * so the caller closes its own dialog and reports the change only when it really happened.
 */
export function useReauthRetry(): {
  run: <T>(action: () => Promise<T>, onReplayed?: (result: T) => void) => Promise<T | null>;
  hold: ReauthHold;
} {
  const { reauthenticate, account } = useAuth();
  const { me } = useTenantInstance();
  const accountEmail = account?.email ?? null;
  const [code, setCode] = useState<"REAUTH_REQUIRED" | "MFA_REQUIRED">("REAUTH_REQUIRED");
  const [blocked, setBlocked] = useState(false);
  const held = useRef<{
    action: () => Promise<unknown>;
    onReplayed?: (result: unknown) => void;
  } | null>(null);
  const [isOpen, setOpen] = useState(false);
  const [isRetrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async <T>(action: () => Promise<T>, onReplayed?: (result: T) => void): Promise<T | null> => {
      try {
        const result = await action();
        held.current = null;
        setOpen(false);
        return result;
      } catch (cause) {
        if (isStepUpRequired(cause)) {
          // Nothing was written: hold the same write for after the sign-in.
          held.current = {
            action,
            onReplayed: onReplayed as ((result: unknown) => void) | undefined,
          };
          const mfa = isMfaRequired(cause);
          const noLoop = mfa && recentStepUpAttempt(accountEmail);
          setCode(mfa ? "MFA_REQUIRED" : "REAUTH_REQUIRED");
          setBlocked(noLoop);
          setError(noLoop ? providerDidNotAskMessage(me?.mfa) : null);
          setOpen(true);
          return null;
        }
        throw cause;
      }
    },
    [accountEmail, me?.mfa],
  );

  const confirm = useCallback(() => {
    const entry = held.current;
    if (!entry || blocked) {
      setOpen(false);
      return;
    }
    if (code === "MFA_REQUIRED") noteStepUpAttempt(accountEmail);
    setRetrying(true);
    void reauthenticate()
      .then(async (outcome) => {
        if (outcome === "redirecting") return;
        if (outcome === "failed") {
          setError("Your identity was not confirmed. Try again, or cancel and come back later.");
          return;
        }
        try {
          const result = await entry.action();
          held.current = null;
          setOpen(false);
          clearStepUpAttempt();
          entry.onReplayed?.(result);
        } catch (cause) {
          if (isMfaRequired(cause)) {
            // The provider came back without a second factor: say so, never ask it again.
            setCode("MFA_REQUIRED");
            setBlocked(true);
            setError(providerDidNotAskMessage(me?.mfa));
            return;
          }
          setError(cause instanceof Error ? cause.message : "The change could not be saved.");
        }
      })
      // The step could not start: plain words, never Microsoft's text (#2006).
      .catch((cause: unknown) => {
        // Nobody was sent to the provider: the "just asked" marker must not block the next try.
        clearStepUpAttempt();
        setError(stepUpRefusedMessage(cause));
      })
      .finally(() => setRetrying(false));
  }, [reauthenticate, blocked, code, accountEmail, me?.mfa]);

  const cancel = useCallback(() => {
    held.current = null;
    setOpen(false);
    setError(null);
    setBlocked(false);
  }, []);

  return {
    run,
    hold: {
      isOpen,
      isRetrying,
      error,
      confirm,
      cancel,
      code,
      blocked,
      remember: { days: me?.mfa?.rememberDays ?? 0, account: accountEmail },
    },
  };
}

/** The writes of the Directory groups tab and of "Change role". */
export function useGroupAccessMutations(tenantId: string | null) {
  const client = useQueryClient();
  const { selectedInstance } = useTenantInstance();

  const invalidate = useCallback(() => {
    void client.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return (
          typeof key === "string" &&
          (key.includes("/group-mappings") ||
            key.startsWith("/api/v2/access/users") ||
            key.startsWith("/api/v2/platform/users") ||
            (key.startsWith("/api/v2/tenants/") && key.includes("/users")))
        );
      },
    });
  }, [client]);

  const createMapping = useMutation({
    mutationFn: (body: CreateGroupMappingBody) =>
      apiRequest(groupMappingsPath(tenantId!), { method: "POST", body }),
    onSuccess: invalidate,
  });

  const removeMapping = useMutation({
    mutationFn: ({ mappingId, reason }: { mappingId: string; reason?: string }) =>
      apiRequest<void>(`${groupMappingsPath(tenantId!)}/${mappingId}`, {
        method: "DELETE",
        body: reason ? { reason } : {},
      }),
    onSuccess: invalidate,
  });

  const setEnforcement = useMutation({
    mutationFn: ({ enabled, reason }: { enabled: boolean; reason: string }) =>
      apiRequest<GroupMappingsResponse>(`/api/v2/tenants/${tenantId}/group-enforcement`, {
        method: "PUT",
        body: { enabled, reason },
      }),
    onSuccess: invalidate,
  });

  const setOverride = useMutation({
    mutationFn: ({ userId, roleName }: { userId: string; roleName: OverrideRole }) =>
      apiRequest(`/api/v2/access/group-members/${userId}/override`, {
        method: "PUT",
        body: { roleName },
        headers: instanceHeaders(selectedInstance?.id),
      }),
    onSuccess: invalidate,
  });

  const clearOverride = useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      apiRequest<void>(`/api/v2/access/group-members/${userId}/override`, {
        method: "DELETE",
        body: {},
        headers: instanceHeaders(selectedInstance?.id),
      }),
    onSuccess: invalidate,
  });

  return { createMapping, removeMapping, setEnforcement, setOverride, clearOverride };
}
